package com.siriusxm.cmc.views

import androidx.compose.runtime.Composable
import com.siriusxm.pia.Application
import com.siriusxm.pia.ApplicationDescriptor
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.border
import com.siriusxm.pia.components.svgIcon
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Styles for the application list component.
 */
object ApplicationListStyles : StyleSheet() {
    val applicationList by style {
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
    }

    val starButton by style {
        position(Position.Absolute)
        top(8.px)
        right(8.px)
        padding(4.px)
        cursor("pointer")
        borderRadius(4.px)
        opacity(0)
        property("transition", "opacity 0.15s ease")

        self + hover style {
            backgroundColor(SXMUI.borderDividerDefault.value())
        }
    }

    val starButtonVisible by style {
        opacity(1)
    }

    val applicationItem by style {
        position(Position.Relative)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(1.cssRem)
        border(bottom = 1.px, color = SXMUI.borderDividerDefault.value())
        cursor("pointer")

        type("h4") style {
            fontSize((1.2).em)
            fontWeight(700)
            margin(0.px, 0.px, 4.px, 0.px)
        }

        self + hover style {
            backgroundColor(SXMUI.borderDividerDefault.value())

            // Show star button on hover
            className(starButton) style {
                opacity(1)
            }
        }
    }

    // Decorative application icon shown at the start of each list row.
    val applicationIcon by style {
        property("flex", "0 0 auto")
        width(32.px)
        height(32.px)
    }

    // Wraps the name/description so they stack beside the icon.
    val applicationBody by style {
        property("flex", "1 1 auto")
        property("min-width", "0")
    }

    val applicationDescription by style {
        color(SXMUI.subtleTextColor.value())
        fontSize((0.9).cssRem)
    }
}

/**
 * Renders a list of applications with support for both internal and external apps.
 * External applications display an icon indicating they open in a new tab.
 * Starred applications appear at the top of the list.
 *
 * @param applications The list of application descriptors to display
 */
@Composable
fun ApplicationList(applications: List<ApplicationDescriptor>) {
    Style(ApplicationListStyles)

    val starredApps = rememberStarredApps()

    // Filter to allowed apps and sort starred ones to top
    val allowedApps = applications.filter { app -> Application.hasApplicationAccess(app) }

    val sortedApps = allowedApps.sortedByDescending { starredApps.isStarred(it.path) }

    Div({ classes(ApplicationListStyles.applicationList) }) {
        sortedApps.forEach { app ->
            ApplicationItem(app, starredApps)
        }
    }
}

/**
 * Renders a single application entry in the applications list.
 * Includes a star button (top right) for favoriting and an external link icon for external apps.
 *
 * @param app The application descriptor containing name, description, and path
 * @param starredApps The starred apps state for managing favorites
 */
@Composable
private fun ApplicationItem(app: ApplicationDescriptor, starredApps: StarredAppsState) {
    val isExternal = app.path.startsWith("http")
    val isStarred = starredApps.isStarred(app.path)

    Div({
        classes(ApplicationListStyles.applicationItem)
        if (!isExternal) {
            onClick {
                it.preventDefault()
                Application.navigate(app.path)
            }
        }
    }) {
        // Star button - positioned top right, visible on hover or if starred
        Div({
            classes(ApplicationListStyles.starButton)
            if (isStarred) {
                classes(ApplicationListStyles.starButtonVisible)
            }
            title(if (isStarred) "Remove from favorites" else "Add to favorites")
            onClick { event ->
                event.preventDefault()
                event.stopPropagation()
                starredApps.toggle(app.path)
            }
        }) {
            if (isStarred) {
                svgIcon(SVGIcons.starFilled) {
                    size = 20.px
                    style {
                        color(Color("#FFD700"))
                    }
                }
            } else {
                svgIcon(SVGIcons.star) {
                    size = 20.px
                    style {
                        color(SXMUI.subtleTextColor.value())
                    }
                }
            }
        }

        // Decorative application icon (optional)
        app.image?.let { image ->
            Img(src = image, alt = app.name) { classes(ApplicationListStyles.applicationIcon) }
        }

        // Application content
        Div({ classes(ApplicationListStyles.applicationBody) }) {
            A(if (isExternal) app.path else "/#${app.path}", {
                if (isExternal) {
                    target(ATarget.Blank)
                }
            }) {
                H4 {
                    Text(app.name)
                    if (isExternal) {
                        Span({
                            style {
                                marginLeft(8.px)
                                color(SXMUI.subtleTextColor.value())
                            }
                            title("Opens in new tab")
                        }) {
                            svgIcon(SVGIcons.externalLink) {
                                size = 16.px
                            }
                        }
                    }
                }
            }
            if (app.description != null) {
                Div({ classes(ApplicationListStyles.applicationDescription) }) {
                    Text(app.description!!)
                }
            }
        }
    }
}